import React, { useCallback, useEffect, useRef, useState, useTransition } from "react";
import {
  graphql,
  PreloadedQuery,
  useMutation,
  usePreloadedQuery,
  useQueryLoader,
} from "react-relay";
import { useParams } from "react-router";
import { ContentSuspense } from "src/components/ui/content-suspense";
import { DateTimePicker } from "src/components/ui/datetime-picker";
import DiscordIcon from "src/components/ui/icons/discord";
import SlackIcon from "src/components/ui/icons/slack";
import { PageSuspense } from "src/components/ui/page-suspense";
import { UIConfirmationDialog } from "src/components/ui/UIConfirmationDialog";
import { UITourBanner } from "src/components/ui/UITourBanner";
import { useMe, usePermissions } from "src/context/MeContext";
import { usePusherEvent } from "src/hooks/use-pusher-event";
import { useTeamSelection } from "src/hooks/use-team-selection";
import { useToast } from "src/hooks/use-toast";

import type { CreateOnboardingTaskStatusMutation as CreateOnboardingTaskStatusMutationType } from "../SettingsPage/mutations/__generated__/CreateOnboardingTaskStatusMutation.graphql";
import type { UpdateOnboardingTaskStatusMutation as UpdateOnboardingTaskStatusMutationType } from "../SettingsPage/mutations/__generated__/UpdateOnboardingTaskStatusMutation.graphql";
import { CreateOnboardingTaskStatusMutation } from "../SettingsPage/mutations/CreateOnboardingTaskStatusMutation";
import { UpdateOnboardingTaskStatusMutation } from "../SettingsPage/mutations/UpdateOnboardingTaskStatusMutation";
import { DigestJobDetailsPageQuery } from "./__generated__/DigestJobDetailsPageQuery.graphql";
import { DigestJobDetailsPageRunDigestMutation } from "./__generated__/DigestJobDetailsPageRunDigestMutation.graphql";
import DigestJobInfo from "./components/DigestJobInfo";
import EditDigestJobDialog from "./components/EditDigestJobDialog";
import JobRunsListContainer, { JobRunsListContainerRef } from "./JobRunsListContainer";

// Main query to fetch job and its runs
const DigestJobDetailsPageQueryDocument = graphql`
  query DigestJobDetailsPageQuery($id: GlobalID!) {
    me {
      ...appMe
      organization {
        id
        onboardingTaskStatuses {
          edges {
            node {
              id
              taskId
              status
            }
          }
        }
        integrations {
          edges {
            node {
              integrationType
              integrationStatus
            }
          }
        }
        discordWebhooks: notificationIntegrations(kind: DISCORD_WEBHOOK) {
          edges {
            node {
              id
              name
            }
          }
        }
      }
    }
    slackChannels(types: "public_channel,private_channel") {
      id
      name
    }
    node(id: $id) {
      ... on GraphQLJob {
        id
        name
        inputJson
        team {
          id
        }
        notificationIntegrations {
          id
          name
          kind
          slackChannelId
        }
        ...DigestJobInfo_job
      }
    }
  }
`;

// Mutation to run digest job
const RunDigestJobMutationDocument = graphql`
  mutation DigestJobDetailsPageRunDigestMutation($input: RunDigestJobInput!) {
    runDigestJob(input: $input) {
      success
      job {
        id
      }
    }
  }
`;

// Component that renders the job details content
function DigestJobContent({
  queryRef,
  onRefetch,
}: {
  queryRef: PreloadedQuery<DigestJobDetailsPageQuery>;
  onRefetch?: () => void;
}) {
  const { toast } = useToast();
  const me = useMe();
  const { canWrite } = usePermissions();
  const canModify = canWrite("digests");
  const { teamId } = useTeamSelection({ userId: me.id, initialTeamContext: me.teamContext });
  const jobRunsListRef = useRef<JobRunsListContainerRef>(null);

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isRunNowConfirmOpen, setIsRunNowConfirmOpen] = useState(false);
  const [runMode, setRunMode] = useState<"now" | "schedule">("now");
  const [scheduledDateTime, setScheduledDateTime] = useState<string>("");
  const [runDigestJob, isRunningDigest] = useMutation<DigestJobDetailsPageRunDigestMutation>(RunDigestJobMutationDocument);
  const [createTaskStatus] = useMutation<CreateOnboardingTaskStatusMutationType>(CreateOnboardingTaskStatusMutation);
  const [updateTaskStatus] = useMutation<UpdateOnboardingTaskStatusMutationType>(UpdateOnboardingTaskStatusMutation);

  // Check for onboarding mode and step
  const [onboardingStep, setOnboardingStep] = useState<number | null>(null);
  const [hasJustRunDigest, setHasJustRunDigest] = useState(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const stepParam = urlParams.get("onboarding");
    if (stepParam) {
      const step = parseInt(stepParam, 10);
      setOnboardingStep(isNaN(step) ? null : step);
    }

    // Check for edit parameter to auto-open edit dialog
    if (urlParams.get("edit") === "true") {
      setIsEditDialogOpen(true);
      // Clean up URL after opening dialog
      const newUrl = stepParam
        ? `${window.location.pathname}?onboarding=${stepParam}`
        : window.location.pathname;
      window.history.replaceState({}, "", newUrl);
    }
  }, []);

  const isOnboarding = onboardingStep !== null;

  // Check if task is already completed by looking at task statuses
  const isTaskCompleted = () => {
    const taskStatuses = data.me.organization?.onboardingTaskStatuses?.edges || [];
    const taskStatusNode = taskStatuses.find(edge => edge.node.taskId === "CREATE_AND_RUN_FIRST_DIGEST");
    return taskStatusNode?.node.status === "COMPLETED";
  };

  // Determine banner text based on state
  const getOnboardingBannerText = () => {
    // If user just ran the digest in this session
    if (hasJustRunDigest) {
      return "Excellent! Your digest agent is running. You can view the results below once it completes.";
    }

    // If task is already completed (viewing existing digest)
    if (isTaskCompleted()) {
      return "Great! This is your first digest agent. You can run it again anytime or view past results below.";
    }

    // Task not completed yet (just created)
    return "Great! Your digest agent is ready. Click 'Run Now' to test it and see the results.";
  };

  // Determine if we should show the return-to-onboarding button for the banner
  const shouldShowBackButton = () => {
    // Only show back button if user has run the digest (task completed or just ran it)
    return isOnboarding && (hasJustRunDigest || isTaskCompleted());
  };

  const data = usePreloadedQuery<DigestJobDetailsPageQuery>(
    DigestJobDetailsPageQueryDocument,
    queryRef
  );

  const job = data.node;
  const organization = data.me.organization;

  // Check for integrations
  const hasSlackIntegration = organization?.integrations?.edges?.some(
    (edge: any) => edge.node.integrationType === "SLACK" && edge.node.integrationStatus === "APPROVED"
  ) ?? false;

  const hasDiscordIntegration = organization?.integrations?.edges?.some(
    (edge: any) => edge.node.integrationType === "DISCORD" && edge.node.integrationStatus === "APPROVED"
  ) ?? false;

  const allSlackChannels = data.slackChannels || [];

  const runNowSlackChannels = React.useMemo(() => {
    if (!job) {
      return [];
    }

    const slackChannelNIs =
      job.notificationIntegrations?.filter(
        (ni: any) => ni.kind === "SLACK_CHANNEL" && ni.slackChannelId
      ) || [];

    const slackChannels = slackChannelNIs
      .map((ni: any) => {
        const channelId = ni.slackChannelId as string | null;
        if (!channelId) {
          return null;
        }
        // Look up the actual channel name from allSlackChannels
        const channelData = allSlackChannels.find((ch: any) => ch.id === channelId);
        return {
          id: channelId,
          name: channelData?.name || channelId,
        };
      })
      .filter((channel): channel is { id: string; name: string } => !!channel);

    if (slackChannels.length > 0) {
      return slackChannels;
    }

    const inputJson = job.inputJson as Record<string, any> | undefined;
    const hasNotificationIntegrationIds =
      inputJson && "notification_integration_ids" in inputJson;
    const legacyChannelId =
      !hasNotificationIntegrationIds && inputJson?.slack_channel_id
        ? (inputJson.slack_channel_id as string)
        : null;

    if (legacyChannelId) {
      // Look up the actual channel name from allSlackChannels
      const channelData = allSlackChannels.find((ch: any) => ch.id === legacyChannelId);
      return [
        {
          id: legacyChannelId,
          name: channelData?.name || legacyChannelId,
        },
      ];
    }

    return [];
  }, [job, allSlackChannels]);

  const runNowDiscordWebhooks = React.useMemo(() => {
    if (!job) {
      return [];
    }

    const discordNIs =
      job.notificationIntegrations?.filter(
        (ni: any) => ni.kind === "DISCORD_WEBHOOK"
      ) || [];

    return discordNIs.map((ni: any) => ({
      id: ni.id,
      name: ni.name,
    }));
  }, [job]);

  // Check if scheduled time has an error (is in the past)
  const hasScheduleError = React.useMemo(() => {
    if (runMode !== "schedule" || !scheduledDateTime) return false;
    const scheduledDate = new Date(scheduledDateTime);
    const now = new Date();
    return scheduledDate.getTime() <= now.getTime();
  }, [runMode, scheduledDateTime]);

  // Initialize dialog state when Run dialog opens
  React.useEffect(() => {
    if (isRunNowConfirmOpen && job) {
      // Reset to "now" mode
      setRunMode("now");
      // Set default scheduled time to nearest next full hour
      const now = new Date();
      const nextHour = new Date(now);
      nextHour.setHours(now.getHours() + 1);
      nextHour.setMinutes(0);
      nextHour.setSeconds(0);
      nextHour.setMilliseconds(0);
      const localDateTime = new Date(nextHour.getTime() - nextHour.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16);
      setScheduledDateTime(localDateTime);
    }
  }, [isRunNowConfirmOpen, job]);

  if (!job) {
    return (
      <div className="p-8 text-center">
        <h3 className="text-xl font-semibold text-foreground mb-2">
          Digest job not found
        </h3>
      </div>
    );
  }

  const handleRefresh = () => {
    // Trigger refetch from parent component
    onRefetch?.();
  };

  const handleEdit = () => {
    setIsEditDialogOpen(true);
  };

  const handleRunNow = () => {
    setIsRunNowConfirmOpen(true);
  };

  // Mark onboarding task as completed
  const markOnboardingTaskComplete = async () => {
    try {
      // Find the CREATE_AND_RUN_FIRST_DIGEST task status
      const taskStatuses = data.me.organization?.onboardingTaskStatuses?.edges || [];
      const taskStatusNode = taskStatuses.find(edge => edge.node.taskId === "CREATE_AND_RUN_FIRST_DIGEST");

      if (taskStatusNode) {
        // Task exists, update it
        await updateTaskStatus({
          variables: {
            input: {
              id: taskStatusNode.node.id,
              status: "COMPLETED"
            }
          }
        });
      } else {
        // Task doesn't exist, create it
        // Get teamId from the digest job
        const jobTeamId = data.node?.team?.id;
        if (jobTeamId) {
          await createTaskStatus({
            variables: {
              input: {
                teamId: jobTeamId,
                taskId: "CREATE_AND_RUN_FIRST_DIGEST",
                status: "COMPLETED"
              }
            }
          });
        }
      }
    } catch (error) {
      console.error("Failed to mark onboarding task as completed:", error);
      // Don't fail the main flow if this fails
    }
  };

  const handleRunNowConfirm = () => {
    // Validate scheduled time is in the future
    if (runMode === "schedule" && scheduledDateTime) {
      const scheduledDate = new Date(scheduledDateTime);
      const now = new Date();
      if (scheduledDate.getTime() <= now.getTime()) {
        toast({
          title: "Invalid time",
          description: "Scheduled time must be in the future.",
          variant: "error",
        });
        return;
      }
    }

    setIsRunNowConfirmOpen(false);

    // Always send to configured channels/webhooks
    const slackChannelIds = runNowSlackChannels.map(ch => ch.id);
    const discordWebhookIds = runNowDiscordWebhooks.map(wh => wh.id);

    // Calculate run_date for scheduling mode
    let runDateTimestamp: number | null = null;
    if (runMode === "schedule" && scheduledDateTime) {
      const scheduledDate = new Date(scheduledDateTime);
      runDateTimestamp = Math.floor(scheduledDate.getTime() / 1000);
    }

    runDigestJob({
      variables: {
        input: {
          jobId: job.id,
          slackChannelIds: slackChannelIds,
          notificationIntegrationIds: discordWebhookIds,
          runDate: runDateTimestamp,
        },
      },
      onCompleted: (response) => {
        if (response.runDigestJob.success) {
          const isScheduled = runMode === "schedule";
          toast({
            title: isScheduled ? "Digest Agent scheduled" : "Digest Agent started",
            description: isScheduled
              ? "The digest agent has been successfully scheduled."
              : "The digest agent has been successfully started.",
            variant: "success",
          });

          // Update onboarding state and mark task as completed (only for immediate runs)
          if (!isScheduled && isOnboarding) {
            setHasJustRunDigest(true);
            markOnboardingTaskComplete();
          }

          // Add delay to allow backend to process before refetching
          setTimeout(() => {
            jobRunsListRef.current?.refresh();
            handleRefresh(); // Refresh job data to get updated schedule
          }, 1000);
        } else {
          toast({
            title: "Error",
            description: runMode === "schedule"
              ? "Failed to schedule the digest. Please try again."
              : "Failed to run the digest. Please try again.",
            variant: "error",
          });
        }
      },
      onError: (error) => {
        toast({
          title: "Error",
          description: error.message || "An unexpected error occurred",
          variant: "error",
        });
      },
    });
  };


  return (
    <>
      <div className="h-full w-full overflow-y-auto custom-scrollbar">
        <div className="p-4 sm:p-6">
          {/* Onboarding Banner */}
          {isOnboarding && (
            <UITourBanner
              text={getOnboardingBannerText()}
              showBackButton={shouldShowBackButton()}
              onboardingStep={onboardingStep}
            />
          )}

          <DigestJobInfo
            job={job}
            isRunningDigest={isRunningDigest}
            onRunNow={handleRunNow}
            onEdit={handleEdit}
            onStatusChanged={handleRefresh}
            canModify={canModify}
          />

          <div className="mt-6">
            <JobRunsListContainer
              ref={jobRunsListRef}
              jobId={job.id}
            />
          </div>
        </div>
      </div>

      {/* Edit Digest Job Dialog */}
      {isEditDialogOpen && (
        <PageSuspense>
          <EditDigestJobDialog
            jobId={job.id}
            isOpen={isEditDialogOpen}
            onClose={() => {
              setIsEditDialogOpen(false);
              handleRefresh(); // Refresh data after edit
            }}
            teamId={teamId}
            organizationId={data.me.organization.id}
          />
        </PageSuspense>
      )}

      {/* Run Confirmation Dialog */}
      <UIConfirmationDialog
        isOpen={isRunNowConfirmOpen}
        onClose={() => setIsRunNowConfirmOpen(false)}
        onConfirm={handleRunNowConfirm}
        title="Run Digest Agent"
        description="Run digest agent immediately or schedule to run it at a specific time."
        confirmText={runMode === "now" ? "Run Now" : "Schedule"}
        cancelText="Cancel"
        variant="default"
        isLoading={isRunningDigest}
        confirmDisabled={hasScheduleError}
        contentClassName="sm:max-w-2xl"
      >
        {/* Horizontal divider */}
        <div className="border-t border-border my-4" />

        <div className="space-y-4">
          {/* Run Mode Selection */}
          <div className="space-y-2">
            <div className="text-sm font-medium text-muted-foreground">When to Run</div>
            <div className="flex gap-4">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="runMode"
                  value="now"
                  checked={runMode === "now"}
                  onChange={() => setRunMode("now")}
                  className="w-4 h-4 text-blue-500"
                />
                <span className="text-sm text-muted-foreground">Run now</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="runMode"
                  value="schedule"
                  checked={runMode === "schedule"}
                  onChange={() => setRunMode("schedule")}
                  className="w-4 h-4 text-blue-500"
                />
                <span className="text-sm text-muted-foreground">Schedule for later</span>
              </label>
            </div>

            {/* Date/Time Picker for Schedule Mode */}
            {runMode === "schedule" && (
              <div className="mt-3">
                <label className="block text-sm text-muted-foreground mb-2">
                  Date and Time (Local Timezone)
                </label>
                <DateTimePicker
                  value={scheduledDateTime}
                  onChange={setScheduledDateTime}
                />
              </div>
            )}
          </div>

          {/* Horizontal divider */}
          <div className="border-t border-border my-4" />

          {/* Notifications Section */}
          <div>
            <div className="text-sm font-medium text-muted-foreground">Notifications</div>
            <p className="text-sm text-muted-foreground mb-3">
              Configure notifications from Edit Digest Agent.
            </p>

            <div className="space-y-4">
              {!hasSlackIntegration && !hasDiscordIntegration ? (
                <div className="text-sm text-muted-foreground">
                  Connect Slack or Discord in Integrations to send notifications.
                </div>
              ) : (
                <>
                  {/* Slack Options */}
                  {hasSlackIntegration && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                        <SlackIcon className="h-5 w-5" />
                        <span>Slack</span>
                      </div>
                      <div className="ml-7">
                        {runNowSlackChannels.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {runNowSlackChannels.map((channel) => (
                              <div
                                key={channel.id}
                                className="flex items-center gap-1.5 px-2 py-1 bg-accent border border-border rounded-md text-sm"
                              >
                                <span className="text-muted-foreground">#</span>
                                <span className="text-foreground">{channel.name}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-xs text-muted-foreground">
                            No Slack channels configured yet.
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Discord Options */}
                  {hasDiscordIntegration && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                        <DiscordIcon className="h-5 w-5" />
                        <span>Discord</span>
                      </div>
                      <div className="ml-7">
                        {runNowDiscordWebhooks.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {runNowDiscordWebhooks.map((webhook) => (
                              <div
                                key={webhook.id}
                                className="flex items-center gap-1.5 px-2 py-1 bg-accent border border-border rounded-md text-sm"
                              >
                                <DiscordIcon className="h-4 w-4" />
                                <span className="text-foreground">{webhook.name}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-xs text-muted-foreground">
                            No Discord webhooks configured yet.
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </UIConfirmationDialog>
    </>
  );
}

// Main component with data loading
export default function DigestJobDetailsPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const [queryRef, loadQuery] = useQueryLoader<DigestJobDetailsPageQuery>(
    DigestJobDetailsPageQueryDocument
  );
  const [, startTransition] = useTransition();

  React.useEffect(() => {
    if (jobId) {
      loadQuery({ id: jobId });
    }
  }, [jobId, loadQuery]);

  const handleRefetch = useCallback(() => {
    if (jobId) {
      startTransition(() => {
        loadQuery({ id: jobId }, { fetchPolicy: "network-only" });
      });
    }
  }, [jobId, loadQuery, startTransition]);

  usePusherEvent<{ job_id: string }>({
    channelId: jobId,
    eventType: "DIGEST_FEEDBACK_UPDATED",
    onEvent: handleRefetch,
  });

  if (!queryRef) {
    return (
      <div className="p-8 text-center">
        <div className="text-muted-foreground">Loading digest job...</div>
      </div>
    );
  }

  return (
    <ContentSuspense height="h-full">
      <DigestJobContent queryRef={queryRef} onRefetch={handleRefetch} />
    </ContentSuspense>
  );
}
